import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { Form } from "react-bootstrap";
import * as Yup from "yup";
import golden from "../../../../../../assets/logos/golden-logo.png";
import silver from "../../../../../../assets/logos/silver-logo.png";
import bitcoin from "../../../../../../assets/icons/asset-icons/bitcoin.svg";
import ETHIcon from "../../../../../../assets/icons/asset-icons/ETHIcon.svg";
import Button from "../../../../../common/Button/Button";
import Select from "../../../../../common/Formik/Select/Select";
import styles from "../P2pTab/Buy/Buy.module.scss";
import InputFormik from "../../../../../common/Formik/Input/InputFormik";
import ConversionModal from "../../../../../common/Modals/ConversionModal/ConversionModal";
import { useSelector, useDispatch } from "react-redux";
import {
  getTokenBalanceAction,
  convertCoinAction,
  getUserCoinListAction,
} from "../../../../../../features/user/userSlice";
import { validationMessage } from "../../../../../../Helper/helperfunction";
import { Messages, FORMIK_REGEX } from "../../../../../../utils/constants";
import { toasts } from "../../../../../common/Toast/Toast";
import PaymentCompleted from "../../../../../common/Modals/PaymentCompleted/PaymentCompleted";
import { formatPrice, toFix } from "../../../../../../utils/utils";
import { STATUS_CODE } from "../../../../../../utils/constants";

const assetOptions = [
  {
    label: "Gold STBL",
    icon: golden,
    value: 1,
    symbol: "XAU/USD",
  },
  {
    label: "Silver STBL",
    icon: silver,
    value: 2,
    symbol: "XAG/USD",
  },
];

const toOptions = [
  {
    label: "ETH",
    icon: ETHIcon,
    value: 3,
    symbol: "ETH/USD",
  },
  {
    label: "BTC",
    icon: bitcoin,
    value: 4,
    symbol: "BTC/USD",
  },
  ...assetOptions,
];

const Convert = () => {
  const dispatch = useDispatch();
  const [coinLists, setCoinList] = useState([]);
  const cryptoPrices = useSelector((state) => state?.users?.cryptoPrice);
  const [tokenSymbol, setTokenSymbol] = useState(assetOptions[0]);
  const [firstToken, setFirstToken] = useState(assetOptions[0]);
  const [secondToken, setSecondToken] = useState({});
  const [tokenOneBalance, setTokenOneBalance] = useState(0);
  const [tokenTwoBalance, setTokenTwoBalance] = useState(0);
  const [newAssetOptions, setNewAssetOptions] = useState([]);
  const [approxTokens, setApproxTokens] = useState(0);
  const [convertToGoldOrSilver, setConvertToGoldOrSilver] = useState({
    convertToGold: false,
    convertToSilver: true,
  });

  const [convertData, setConvertData] = useState(null);
  const [decimalLimit, setDecimalLimit] = useState();

  const spotPrice = useSelector((state) => state?.users?.spotPrice);

  const [show, setShow] = useState(false);
  const [showTxnDone, setShowTxnDone] = useState(false);
  const kycStatus = useSelector(
    (state) => state?.persist?.userInfo?.kyc_status
  );

  const fetchCoinOptions = async () => {
    try {
      const response = await dispatch(getUserCoinListAction());
      if (response.data.status === STATUS_CODE.successful) {
        const array = [];
        const newData = response.data.data.coinList;
        setCoinList(newData);
        const quantityDecimal = newData.find(
          (coin) => coin.coin_symbol === firstToken?.label
        );
        setDecimalLimit(quantityDecimal?.quantity_decimal);
      }
    } catch (error) {
      console.error("Error fetching coin options:", error);
    }
  };

  const getBalance = async () => {
    try {
      const params = {
        tokenId: tokenSymbol?.value,
      };
      const goldBalance = await dispatch(getTokenBalanceAction(params));
      const silverBalance = await dispatch(
        getTokenBalanceAction({ tokenId: 2 })
      );

      if (firstToken.value === 1) {
        setTokenOneBalance(goldBalance.data.data.quantity);
        setTokenTwoBalance(silverBalance.data.data.quantity);
        setConvertToGoldOrSilver({
          convertToGold: false,
          convertToSilver: true,
        });
      } else {
        setTokenOneBalance(silverBalance.data.data.quantity);
        setTokenTwoBalance(goldBalance.data.data.quantity);
        setConvertToGoldOrSilver({
          convertToGold: true,
          convertToSilver: false,
        });
      }
    } catch (error) {
      console.log("Error fetching token balance:", error);
    }
  };

  const filterTokenListToConvert = () => {
    const newList = toOptions.filter(
      (token) => token.value !== firstToken.value
    );
    setNewAssetOptions(newList);
    setSecondToken(newList[0]);
  };

  const convertValidationSchema = Yup.object({
    quantity: Yup.string()
      .required(validationMessage("Quantity"))
      .matches(FORMIK_REGEX.NONZERODECIMAL, Messages?.NONZERODECIMALVALIDATION)
      // .test("is-decimal", "Two decimal places allowed", (val) => {
      //   if (val !== undefined) {
      //     return FORMIK_REGEX.TWODECIMALNUMBER.test(val);
      //   }
      //   return true;
      // })
      .test("checkBalance", "Quantity exceeds token balance!", (val) => {
        if (val > Number(tokenOneBalance)) {
          return false;
        }
        return true;
      }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      quantity: "",
      goldTokenId: 1,
      silverTokenId: 2,
      ...convertToGoldOrSilver,
    },
    validationSchema: convertValidationSchema,
    onSubmit: async (values) => {
      const convertToBtc = secondToken.value === 4;
      const convertToEth = secondToken.value === 3;
      const goldToCrypto =
        firstToken.value === 1 && (convertToBtc || convertToEth);
      const silverToCrypto =
        firstToken.value === 2 && (convertToBtc || convertToEth);

      setConvertData({
        ...values,
        quantity: Number(values.quantity),
        isCrypto: convertToBtc || convertToEth,
        convertToBtc,
        convertToEth,
        goldToCrypto,
        silverToCrypto,
      });

      setShow(true);
    },
  });

  const checkConversion = async () => {
    if (formik.values.quantity > 0) {
      const quantity = formik.values.quantity;
      let conversionValue = 0;

      const fromPrice =
        firstToken.value === 1 ? spotPrice.goldPrice : spotPrice.silverPrice;

      const toPrice =
        secondToken.value === 1
          ? spotPrice.goldPrice
          : secondToken.value === 2
            ? spotPrice.silverPrice
            : secondToken.value === 3
              ? cryptoPrices.ethPrice
              : cryptoPrices.btcPrice;

      conversionValue = (fromPrice / toPrice) * quantity;
      setApproxTokens(conversionValue);
    } else {
      setApproxTokens("");
    }
  };

  const convertTokens = async () => {
    try {
      const res = await dispatch(convertCoinAction(convertData));
      getBalance();
      setShow(false);
      setShowTxnDone(true);
    } catch (error) {
      toasts.error(error);
    }
  };

  useEffect(() => {
    checkConversion();
  }, [
    formik.values.quantity,
    spotPrice,
    cryptoPrices,
    firstToken,
    secondToken,
  ]);
  const handleQuantityChange = (e) => {
    const filterVal = e.target.value.replace(/[^0-9.]/g, "");
    const value = filterVal;
    const regex = new RegExp(`^\\d*(\\.\\d{0,${decimalLimit}})?$`);
    if (value === "" || regex.test(value)) {
      formik.setFieldValue("quantity", value);
    }
  };

  useEffect(() => {
    getBalance();
    fetchCoinOptions();
    filterTokenListToConvert();
  }, [firstToken]);

  return (
    <>
      <div className={styles.buy_module}>
        <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}>
          <div className={`${styles.buy_module_asset} z-lg ${styles.mb_16}`}>
            <div className={styles.buy_module_asset_header}>
              <div className="d-flex align-items-center">
                <h6 className={styles.select_asset}>From</h6>
                <p className={styles.spot_price}>
                  Current Price: $
                  {firstToken.value === 1
                    ? spotPrice?.goldPrice
                    : spotPrice?.silverPrice}
                </p>
              </div>
              <h6 className={styles.balance}>
                Bal: <span>{toFix(Number(tokenOneBalance), 3)} ozt</span>
              </h6>
            </div>
            <Select
              options={assetOptions}
              variant="dark large_height"
              isSearchable={false}
              value={firstToken}
              onChange={(selected) => {
                formik?.setFieldValue("quantity", "");
                setFirstToken(selected);
                setApproxTokens(0);
              }}
            />
          </div>
          <div className={`${styles.buy_module_asset} ${styles.mb_16}`}>
            <div className={styles.buy_module_asset_header}>
              <div className="d-flex align-items-center">
                <h6 className={styles.select_asset}>To</h6>
                <p className={styles.spot_price}>
                  Current Price: $
                  {secondToken.value === 1
                    ? spotPrice?.goldPrice
                    : secondToken.value === 2
                      ? spotPrice?.silverPrice
                      : secondToken.value === 3
                        ? formatPrice(cryptoPrices.ethPrice)
                        : formatPrice(cryptoPrices.btcPrice)}
                </p>
              </div>
              <h6 className={styles.balance}>
                Bal:{" "}
                <span>
                  {toFix(
                    secondToken.value === 1
                      ? tokenTwoBalance
                      : secondToken.value === 2
                        ? tokenTwoBalance
                        : coinLists?.find(
                          (coin) => coin.coin_symbol === secondToken.label
                        )?.balance || 0,
                    3
                  )}{" "}
                  {secondToken.value === 1 || secondToken.value === 2
                    ? "ozt"
                    : secondToken.label}{" "}
                </span>
              </h6>
            </div>

            <Select
              options={newAssetOptions}
              variant="dark large_height"
              isSearchable={false}
              value={secondToken}
              onChange={(selected) => {
                formik?.setFieldValue("quantity", "");
                setSecondToken(selected);
                setApproxTokens(0);
              }}
            />
          </div>
          <div className={`${styles.amount} ${styles.mb_16}`}>
            <InputFormik
              id="quantity"
              name="quantity"
              label="Amount"
              variant="dark"
              placeholder="0"
              className={styles.amount_input}
              icon={
                <h5 className={styles.approx_text}>
                  ≈{formatPrice(Number(approxTokens))}{" "}
                  <img src={secondToken.icon} alt={`icon`} />
                </h5>
              }
              formik={formik}
              onChange={handleQuantityChange}
              onBlur={formik.handleBlur}
              // value={formik?.values?.quantity}
              value={
                formik.values.quantity ? `${formik.values.quantity} ozt` : ""
              }
              maxLength={14}
            />
          </div>
          <div className={`${styles.total_text} ${styles.mb_16}`}>
            <h6>Total</h6>
            <h4>{formik.values.quantity}</h4>
          </div>
          <Button
            disabled={kycStatus !== "APPROVED"}
            fluid
            type="button"
            onClick={(e) => {
              formik.handleSubmit(e);
            }}
          >
            Convert to {secondToken.label}
          </Button>
        </form>
      </div>
      <ConversionModal
        show={show}
        handleClose={() => setShow(false)}
        quantity={formik.values.quantity}
        firstToken={firstToken}
        secondToken={secondToken}
        spotPrice={spotPrice}
        cryptoPrices={cryptoPrices}
        approxTokens={approxTokens}
        convertTokens={convertTokens}
      />
      <PaymentCompleted
        show={showTxnDone}
        handleClose={() => setShowTxnDone(false)}
        paymentType="MARKETPLACE"
      />
    </>
  );
};

export default Convert;
